import { useState, useCallback } from 'react';
import axios from 'axios';
import { LISTING_ENDPOINTS, CLIENT_ENDPOINTS } from '../constants/api';
import type {
  ListingTypeOption,
  ListingClientType,
  ListingDocumentType,
  ListingClientDocumentType,
  ListingSummary,
  ListingDetail,
  ListingFilters,
  CreateListingPayload,
  UpdateListingPayload,
  UseListingsDataReturn,
} from '../types/ListingTypes';

export type {
  ListingStatus,
  ListingType,
  MaritalStatus,
  ListingImageType,
  ListingTypeOption,
  ListingClientType,
  ListingDocumentType,
  ListingClientDocumentType,
  ListingClient,
  ListingDocument,
  ListingImage,
  ListingAgent,
  ListingSummary,
  ListingDetail,
  ListingFilters,
  CreateListingPayload,
  UpdateListingPayload,
  UseListingsDataReturn,
} from '../types/ListingTypes';

const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

export const useListingsData = (): UseListingsDataReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withLoading = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'An unexpected error occurred.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Reference data ───────────────────────────────────────────────────────────

  const fetchListingTypes = useCallback((): Promise<ListingTypeOption[]> =>
    withLoading(async () => {
      const res = await axios.get(LISTING_ENDPOINTS.TYPES, { headers: authHeader() });
      return (res.data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        createdAt: t.created_at,
      }));
    }), [withLoading]);

  const fetchClientTypes = useCallback((listingTypeId?: string): Promise<ListingClientType[]> =>
    withLoading(async () => {
      const res = await axios.get(LISTING_ENDPOINTS.CLIENT_TYPES, {
        headers: authHeader(),
        params: listingTypeId ? { listingTypeId } : undefined,
      });
      return (res.data || []).map((ct: any) => ({
        id: ct.id,
        name: ct.name,
        listingType: ct.listing_type,
        createdAt: ct.created_at,
      }));
    }), [withLoading]);

  const fetchDocumentTypes = useCallback((listingTypeId?: string): Promise<ListingDocumentType[]> =>
    withLoading(async () => {
      const res = await axios.get(LISTING_ENDPOINTS.DOCUMENT_TYPES, {
        headers: authHeader(),
        params: listingTypeId ? { listingTypeId } : undefined,
      });
      return (res.data || []).map((dt: any) => ({
        id: dt.id,
        name: dt.name,
        multipleUploads: dt.multiple_uploads ?? dt.multipleUploads,
        listingType: dt.listing_type,
        createdAt: dt.created_at,
      }));
    }), [withLoading]);

  const fetchClientDocumentTypes = useCallback((clientTypeId?: string): Promise<ListingClientDocumentType[]> =>
    withLoading(async () => {
      const res = await axios.get(CLIENT_ENDPOINTS.DOCUMENT_TYPES, {
        headers: authHeader(),
        params: clientTypeId ? { clientTypeId } : undefined,
      });
      return (res.data || []).map((dt: any) => ({
        id: dt.id,
        name: dt.name,
        multipleAllowed: dt.multiple_allowed ?? dt.multipleAllowed,
        clientType: dt.client_type,
        createdAt: dt.created_at,
      }));
    }), [withLoading]);

  // ── Listing lists ────────────────────────────────────────────────────────────

  const fetchUserListings = useCallback((userId: string, filters?: ListingFilters): Promise<ListingSummary[]> =>
    withLoading(async () => {
      const res = await axios.get(LISTING_ENDPOINTS.USER_LISTINGS(userId), {
        headers: authHeader(),
        params: filters,
      });
      return res.data || [];
    }), [withLoading]);

  const fetchAgencyListings = useCallback((agencyId: string, filters?: ListingFilters): Promise<ListingSummary[]> =>
    withLoading(async () => {
      const res = await axios.get(LISTING_ENDPOINTS.AGENCY_LISTINGS(agencyId), {
        headers: authHeader(),
        params: filters,
      });
      return res.data || [];
    }), [withLoading]);

  // ── Single listing ───────────────────────────────────────────────────────────

  const fetchListing = useCallback((id: string): Promise<ListingDetail> =>
    withLoading(async () => {
      const res = await axios.get(LISTING_ENDPOINTS.BY_ID(id), { headers: authHeader() });
      return res.data;
    }), [withLoading]);

  // ── CRUD ─────────────────────────────────────────────────────────────────────

  const createListing = useCallback((payload: CreateListingPayload): Promise<ListingSummary> =>
    withLoading(async () => {
      const res = await axios.post(LISTING_ENDPOINTS.CREATE, payload, { headers: authHeader() });
      return res.data;
    }), [withLoading]);

  const updateListing = useCallback((id: string, payload: UpdateListingPayload): Promise<ListingDetail> =>
    withLoading(async () => {
      const res = await axios.put(LISTING_ENDPOINTS.BY_ID(id), payload, { headers: authHeader() });
      return res.data;
    }), [withLoading]);

  const deleteListing = useCallback((id: string): Promise<void> =>
    withLoading(async () => {
      await axios.delete(LISTING_ENDPOINTS.BY_ID(id), { headers: authHeader() });
    }), [withLoading]);

  const uploadClientDocument = useCallback(
    async (clientId: string, documentTypeId: string, name: string, file: File): Promise<void> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('clientId', clientId);
      formData.append('documentTypeId', documentTypeId);
      formData.append('name', name);
      await axios.post(CLIENT_ENDPOINTS.DOCUMENT_UPLOAD, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
    },
    [],
  );

  const uploadListingDocument = useCallback(
    async (listingId: string, documentTypeId: string, name: string, file: File): Promise<void> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('listingId', listingId);
      formData.append('documentTypeId', documentTypeId);
      formData.append('name', name);
      await axios.post(LISTING_ENDPOINTS.DOCUMENT_UPLOAD, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
    },
    [],
  );

  const uploadListingImage = useCallback(
    async (listingId: string, imageType: string, file: File): Promise<void> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('listingId', listingId);
      formData.append('imageType', imageType);
      await axios.post(LISTING_ENDPOINTS.IMAGE_UPLOAD, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
    },
    [],
  );

  const deleteListingImage = useCallback(
    async (imageId: string): Promise<void> => {
      await axios.delete(LISTING_ENDPOINTS.DELETE_IMAGE(imageId), {
        headers: authHeader(),
      });
    },
    [],
  );

  const deleteListingDocument = useCallback(
    async (documentId: string): Promise<void> => {
      await axios.delete(LISTING_ENDPOINTS.DELETE_DOCUMENT(documentId), {
        headers: authHeader(),
      });
    },
    [],
  );

  const deleteClientDocument = useCallback(
    async (documentId: string): Promise<void> => {
      await axios.delete(CLIENT_ENDPOINTS.DELETE_CLIENT_DOCUMENT(documentId), {
        headers: authHeader(),
      });
    },
    [],
  );

  return {
    loading,
    error,
    fetchListingTypes,
    fetchClientTypes,
    fetchDocumentTypes,
    fetchClientDocumentTypes,
    fetchUserListings,
    fetchAgencyListings,
    fetchListing,
    createListing,
    updateListing,
    deleteListing,
    uploadClientDocument,
    uploadListingDocument,
    uploadListingImage,
    deleteListingImage,
    deleteListingDocument,
    deleteClientDocument,
  };
};


